// Phase 1 of transcript merge v2 (docs/planning/transcript_merge_v2.md).
//
// A pure function that takes:
//   - the current `extracted` blob,
//   - the `user_overrides` map describing per-field user intent, and
//   - a fresh extraction from the transcript,
// and returns:
//   - the merged `extracted` blob,
//   - an updated `user_overrides` map (entries with `status: "cleared"` get
//     wiped once the transcript refills the field), and
//   - the list of conflicts that need user resolution.
//
// Phase 1 callers ignore conflicts and keep the typed value (matches today's
// behavior). Phase 2 wires a `<LeadMergeConflictModal>` that resolves
// conflicts before committing.

import type {
  ExtractedLeadV2,
  LeadUserOverride,
  LeadUserOverrides,
  LockedFields,
} from "@/types/lead";
import { isMeaningful } from "@/lib/lead/utils/isMeaningful";

export type MergeLeadConflict = {
  field: keyof ExtractedLeadV2;
  currentValue: unknown;
  transcriptValue: unknown;
  /**
   * Snapshot of the manual override at conflict time. Carries the
   * `updated_at` + `updated_by_user_id` so the Phase 2 modal can show
   * "you typed this on Tue 2:14pm" attribution.
   */
  override: Extract<LeadUserOverride, { status: "manual" }>;
};

export type MergeLeadResult = {
  nextExtracted: ExtractedLeadV2;
  nextUserOverrides: LeadUserOverrides;
  conflicts: MergeLeadConflict[];
};

export type MergeLeadArgs = {
  baseExtracted: ExtractedLeadV2;
  userOverrides: LeadUserOverrides | undefined;
  /** Legacy lock map. Treated as an implicit `manual` override when set. */
  lockedFields: LockedFields | undefined;
  fromTranscript: Partial<ExtractedLeadV2>;
};

/**
 * Pure merger. Does NOT mutate inputs. See module header for the contract.
 *
 * Decision table per field key:
 *
 *   user_overrides[key].status === "cleared"
 *     → transcript wins. Override entry is dropped (field is no longer
 *       cleared once refilled).
 *
 *   user_overrides[key].status === "manual"
 *     → if values are equal, no-op.
 *     → if values differ, push a `MergeLeadConflict`. Caller resolves.
 *       (Phase 1 callers default to "keep current" — typed wins.)
 *
 *   lockedFields[key] === true (legacy)
 *     → existing wins. No conflict surfaced (legacy leads predate the
 *       Phase 2 modal so we keep their behavior identical to today).
 *
 *   otherwise (no user intent recorded)
 *     → preserve existing meaningful value; otherwise transcript fills.
 *       (Matches today's `mergePreferExistingNonBlank` for backward
 *       compatibility with the auto-extract → manual-edit → re-extract
 *       flow.)
 */
export function mergeLeadFromTranscript(args: MergeLeadArgs): MergeLeadResult {
  const { baseExtracted, userOverrides, lockedFields, fromTranscript } = args;
  const nextExtracted: ExtractedLeadV2 = { ...baseExtracted };
  const nextUserOverrides: LeadUserOverrides = { ...(userOverrides ?? {}) };
  const conflicts: MergeLeadConflict[] = [];

  for (const rawKey of Object.keys(fromTranscript) as Array<keyof ExtractedLeadV2>) {
    const transcriptVal = fromTranscript[rawKey];
    // Never let a blank/null transcript value blow away a meaningful field.
    if (!isMeaningful(transcriptVal)) continue;
    // Phone protection: transcript-extracted phones are notoriously flaky.
    // A transcript phone that doesn't normalize to a valid US format
    // (10 digits, or 11 starting with `1`) is treated as not-meaningful for
    // merge purposes — it never overwrites, never refills a cleared field,
    // and never surfaces a conflict. This protects manually-typed phones
    // (which already go through `formatPhoneStrict` on the UI side) from
    // being replaced by garbage like "I was on 4 calls today" → "4".
    // The previous `applyExtraction` post-merge override is now redundant
    // and has been removed in this slice — the merge handles phone uniformly.
    if (rawKey === "phone" && !isValidTranscriptPhone(transcriptVal)) continue;

    const override = userOverrides?.[rawKey];
    const currentVal = baseExtracted[rawKey];

    if (override?.status === "cleared") {
      assignField(nextExtracted, rawKey, transcriptVal);
      delete nextUserOverrides[rawKey];
      continue;
    }

    if (override?.status === "manual") {
      if (!valuesEqual(currentVal, transcriptVal)) {
        conflicts.push({
          field: rawKey,
          currentValue: currentVal,
          transcriptValue: transcriptVal,
          override,
        });
      }
      continue;
    }

    if (lockedFields?.[rawKey]) {
      // Legacy lock: never overwrite. Preserves existing behavior for leads
      // created before `user_overrides` was introduced.
      continue;
    }

    // No explicit user intent on this field. Match `mergePreferExistingNonBlank`:
    // arrays union, existing meaningful wins, transcript fills when blank.
    if (Array.isArray(currentVal) && Array.isArray(transcriptVal)) {
      assignField(nextExtracted, rawKey, unionArray(currentVal, transcriptVal));
      continue;
    }
    // Dev-sync note 176c3512 — `notes` is the only free-form summary field
    // on the lead; every other key is an atomic identifier (name, phone,
    // addresses, budget, etc.) for which "first non-blank wins" is the
    // correct merge. `notes`, by contrast, naturally grows as the
    // transcript grows: the first debounced extraction off a 5-second
    // snippet returns a one-sentence summary; the next extraction off
    // the full conversation returns a richer one. Treat notes as
    // transcript-wins in the auto path so re-extracting on the complete
    // transcript replaces the early one-sentence summary. Manual edits
    // still protect the field via the `user_overrides` branch above.
    if (rawKey === "notes" && isMeaningful(transcriptVal)) {
      assignField(nextExtracted, rawKey, transcriptVal);
      continue;
    }
    if (isMeaningful(currentVal)) continue;
    assignField(nextExtracted, rawKey, transcriptVal);
  }

  return { nextExtracted, nextUserOverrides, conflicts };
}

/**
 * Resolve a set of conflicts using the caller's chosen strategy. Returned
 * `nextExtracted` is the merge result with conflict resolutions applied; the
 * `nextUserOverrides` map gains/keeps `manual` entries reflecting the chosen
 * values (when the transcript wins, the override is dropped so the field
 * stays "auto" until the user explicitly edits it again).
 */
export function applyConflictResolutions(args: {
  mergeResult: MergeLeadResult;
  resolutions: Record<string, ConflictResolution>;
  actorUserId: string | null;
}): { nextExtracted: ExtractedLeadV2; nextUserOverrides: LeadUserOverrides } {
  const { mergeResult, resolutions, actorUserId } = args;
  const nextExtracted: ExtractedLeadV2 = { ...mergeResult.nextExtracted };
  const nextUserOverrides: LeadUserOverrides = { ...mergeResult.nextUserOverrides };
  const now = new Date().toISOString();

  for (const conflict of mergeResult.conflicts) {
    const choice = resolutions[conflict.field as string] ?? { kind: "keep" };
    if (choice.kind === "keep") {
      // Re-stamp the manual override so the next merge round still treats
      // this field as protected.
      nextUserOverrides[conflict.field] = {
        ...conflict.override,
        updated_at: now,
        updated_by_user_id: actorUserId,
      };
      continue;
    }
    if (choice.kind === "overwrite") {
      assignField(nextExtracted, conflict.field, conflict.transcriptValue);
      delete nextUserOverrides[conflict.field];
      continue;
    }
    if (choice.kind === "merge") {
      assignField(nextExtracted, conflict.field, choice.value);
      nextUserOverrides[conflict.field] = {
        status: "manual",
        value: normalizeOverrideValue(choice.value),
        updated_at: now,
        updated_by_user_id: actorUserId,
      };
    }
  }

  return { nextExtracted, nextUserOverrides };
}

export type ConflictResolution =
  | { kind: "keep" }
  | { kind: "overwrite" }
  | { kind: "merge"; value: string | string[] | null };

function valuesEqual(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  if (a == null && b == null) return true;
  if (Array.isArray(a) && Array.isArray(b)) {
    if (a.length !== b.length) return false;
    const sa = [...a].sort();
    const sb = [...b].sort();
    for (let i = 0; i < sa.length; i += 1) {
      if (sa[i] !== sb[i]) return false;
    }
    return true;
  }
  if (typeof a === "object" && typeof b === "object" && a && b) {
    try {
      return JSON.stringify(a) === JSON.stringify(b);
    } catch {
      return false;
    }
  }
  return false;
}

function unionArray<T>(a: T[], b: T[]): T[] {
  return Array.from(new Set([...a, ...b]));
}

/**
 * Loose US-phone validator used inside the merge function. We don't import
 * `formatPhoneStrict` to keep this module pure / free of unrelated deps;
 * the rule is the same: 10 digits, or 11 digits with a leading `1`.
 */
export function isValidTranscriptPhone(v: unknown): boolean {
  if (typeof v !== "string") return false;
  const digits = v.replace(/\D+/g, "");
  if (digits.length === 10) return true;
  if (digits.length === 11 && digits.startsWith("1")) return true;
  return false;
}

/**
 * Type-erased field assignment helper. `ExtractedLeadV2` is a closed
 * interface so direct index access fails TS strict checks; we go through
 * an intentional `unknown` cast here.
 */
function assignField(target: ExtractedLeadV2, key: keyof ExtractedLeadV2, value: unknown): void {
  (target as unknown as Record<string, unknown>)[key as string] = value;
}

function normalizeOverrideValue(v: unknown): string | string[] | null {
  if (v == null) return null;
  if (typeof v === "string") return v;
  if (Array.isArray(v)) return v.map((it) => String(it));
  return String(v);
}
